import React, { ChangeEvent, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CircularProgress,
  Collapse,
  IconButton,
  Pagination,
  Typography,
  useTheme,
} from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactComponent as MagnifyingGlass } from '../../img/icons/warning-magnifying-glass.svg';
import { ReactComponent as WhiteMagnifyingGlass } from '../../img/icons/warning-magnifying-glass-white.svg';
import { UIContext } from '../../context/UIContext';
import { OBJECT_KEY_ID } from '../../constants';
import { FilterContext } from '../../context/FilterContext';
import { DataGridConfigContext } from '../../context/DataGridConfigContext';

const MobileTableRow = ({
  rowMainContent,
  rowExpandableContent,
  hasMobileExpandableContent,
}: {
  rowMainContent: JSX.Element;
  rowExpandableContent?: JSX.Element;
  hasMobileExpandableContent: boolean;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colorMode } = useContext(UIContext);

  const [open, setOpen] = React.useState(false);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 0,
        borderBottom: `1px ${
          colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p']
        } solid`,
        padding: '0.5rem 1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {rowMainContent}

        {hasMobileExpandableContent && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <IconButton
              aria-label={open ? t('actions.lessInfo') : t('actions.moreInfo')}
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          </Box>
        )}
      </Box>
      {hasMobileExpandableContent && (
        <Collapse in={open} timeout="auto" unmountOnExit sx={{ marginTop: '0.5rem' }}>
          {rowExpandableContent}
        </Collapse>
      )}
    </Card>
  );
};

const MobileDataGrid = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { colorMode } = useContext(UIContext);
  const {
    data,
    metadata,
    isLoading,
    translationKey,
    MobileRowMainContent,
    MobileRowExpandableContent,
    hasMobileExpandableContent,
  } = useContext(DataGridConfigContext);
  const { paginationModel, setPaginationModel } = useContext(FilterContext);

  const handleChangePage = (event: ChangeEvent<unknown> | null, newPage: number) => {
    setPaginationModel({
      page: newPage - 1,
      pageSize: paginationModel.pageSize,
    });
  };

  return isLoading ? (
    <Box
      sx={{ display: 'flex', justifyContent: 'center', alignContent: 'center', marginTop: '1rem' }}
    >
      <CircularProgress
        aria-label="Loading Data"
        aria-live="polite"
        aria-busy={isLoading}
        data-testid="loading-icon"
        sx={{ color: 'text.secondary' }}
      />
    </Box>
  ) : (
    <Box
      sx={{
        marginTop: '2rem',
        width: '100vw',
        marginLeft: '-1rem',
        borderTop: `1px ${
          colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p']
        } solid`,
      }}
    >
      {(metadata?.totalRecords || 0) > 0 ? (
        <Box>
          {data?.map((rowData) => (
            <Box key={rowData[OBJECT_KEY_ID]}>
              <MobileTableRow
                rowMainContent={<MobileRowMainContent data={rowData} />}
                rowExpandableContent={
                  hasMobileExpandableContent && <MobileRowExpandableContent data={rowData} />
                }
                hasMobileExpandableContent={hasMobileExpandableContent}
              />
            </Box>
          ))}
          {(metadata?.totalRecords || 0) > 5 && (
            <Pagination
              count={metadata.totalPages}
              page={paginationModel.page + 1}
              onChange={handleChangePage}
              color="primary"
              sx={{
                '.MuiPagination-ul': { justifyContent: 'center', marginTop: 2 },
              }}
            />
          )}
        </Box>
      ) : (
        <Card
          elevation={0}
          sx={{
            borderRadius: '8px',
            border: `1px ${
              colorMode === 'light' ? theme?.palette?.grey?.['12p'] : theme?.palette?.grey?.['23p']
            } solid`,
            padding: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
            }}
          >
            {colorMode === 'light' ? <MagnifyingGlass /> : <WhiteMagnifyingGlass />}
            <Typography variant="h2">{t(`screens.${translationKey}.noDataYet.title`)}</Typography>
            <Typography variant="body1">
              {t(`screens.${translationKey}.noDataYet.subtitle`)}
            </Typography>
          </Box>
        </Card>
      )}
    </Box>
  );
};
export default MobileDataGrid;
